import { type SxProps, TableBody, TableContainer, TableHead } from '@mui/material'
import TableMU from '@mui/material/Table'
import {
  type ReactNode,
  useEffect,
  useMemo,
  useState,
  useRef,
  type CSSProperties,
  type UIEvent,
} from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import {
  ColumnWidthLock,
  useVirtualRows,
  VirtualSkeletonRow,
  VirtualSpacerRow,
} from 'shared/ui/VirtualTable'
import {
  type IColumn,
  type IRow,
  type IRowDefault,
  type IRowExtractType,
} from 'shared/ui/TableNew/store/types'
import { type IScrollbarProps, Scrollbar } from 'shared/ui/Scrollbar'
import { SpinnerLoader } from 'shared/ui/Loader'
import { TableRowHead } from 'shared/ui/TableNew/ui/TableRowHead/TableRowHead'
import { type TableStore } from 'shared/ui/TableNew/store/tableStore'
import { Pagination } from 'shared/ui/TableNew/ui/Pagination/Pagination'
import { EmptyState, type IDefaultEmptyStateProps, Box } from 'shared/ui'
import {
  ExpandedTableRow,
  getExpandedRowItemGroupRow,
  getExpandedRowItems,
  getRowItemKey,
  type IExpandedRowItem,
  type IExpandedTableRowProps,
  renderExpandedRowItem,
} from 'shared/ui/TableNew/ui/TableRow/ExpandedTableRow'
import { type IHeaderLeftContentProps } from 'shared/ui/TableNew/ui/TableRowHead/HeaderLeftContent/HeaderLeftContent'
import { HeaderSelectedContent } from 'shared/ui/TableNew/ui/TableRowHead/HeaderSelectedContent/HeaderSelectedContent'
import styles from './Table.module.scss'

export type ITableProps<TRow extends IRowDefault> = {
  store: TableStore<TRow>

  rows: IRow<TRow>[]
  loading?: boolean
  hidePagination?: boolean

  activeRowId?: TRow['id']
  inActionId?: number | null
  onRowClick?: (row: TRow) => void
  onRowChildClick?: (row: IRowExtractType<TRow>) => void

  getRowLink?: (row: TRow) => string
  getRowChildLink?: (row: IRowExtractType<TRow>) => string

  columns: IColumn<TRow>[]
  columnsChild?: IColumn<IRowExtractType<TRow>>[]

  hideExpandColumnsIds?: string[]
  pinnedColumnIds?: string[]
  noMinWidthColumns?: boolean

  withCheckbox?: boolean
  withSelectAll?: boolean
  withoutSelectedCount?: boolean

  defaultWidthColumn?: CSSProperties['width']
  defaultMinWidthColumn?: CSSProperties['minWidth']
  defaultMaxWidthColumn?: CSSProperties['maxWidth']
  defaultColumnCellStyle?: CSSProperties
  sxTable?: SxProps
  sxBox?: SxProps
  height?: string

  rightHeaderContent?: ReactNode
  actionsCenter?: ReactNode
  bottomTableContent?: ReactNode

  onChangePagination?: (page: number, pageSize: number) => void

  scrollbarProps?: Partial<Omit<IScrollbarProps, 'ref'>>
  emptyStateProps?: IDefaultEmptyStateProps

  isCheckDate?: boolean

  scrollToTOpTrigger?: string
  isHoveredRowActions?: boolean
  isCursor?: boolean
  headerLeftContentProps?: IHeaderLeftContentProps
  ariaLabel: string
  virtualization?: boolean | ITableVirtualizationOptions
}

export type ITableVirtualizationOptions = {
  estimateRowHeight?: number
  dynamicRowHeight?: boolean
  overscan?: number
  fixedLayout?: boolean
  skeletonWhileScrolling?: boolean
}

const TableContent = observer(
  <TRow extends IRowDefault>({
    store,
    columns,
    columnsChild,
    rows,
    activeRowId,
    rightHeaderContent,
    pinnedColumnIds = [],
    inActionId,
    withCheckbox,
    withSelectAll,
    withoutSelectedCount,
    hideExpandColumnsIds,
    hidePagination,
    isCheckDate,
    defaultWidthColumn,
    defaultMinWidthColumn,
    defaultMaxWidthColumn,
    defaultColumnCellStyle,
    height = '100%',
    onRowClick,
    onRowChildClick,
    getRowLink,
    getRowChildLink,
    sxTable,
    sxBox,
    bottomTableContent,
    scrollToTOpTrigger,
    isHoveredRowActions,
    isCursor,
    headerLeftContentProps,
    scrollbarProps,
    ariaLabel,
    virtualization,
    ...props
  }: ITableProps<TRow>) => {
    const ref = useRef(null)
    const [scrollView, setScrollView] = useState<HTMLElement | null>(null)
    const [isScrollLeft, setIsScrollLeft] = useState(false)
    const { columnsStore, bulkStore } = store
    const withActions = useMemo(() => rows.some((item) => item.withActions), [rows])
    const loading = props.loading || store.loading
    const selected = store.bulkStore.isSelected || withoutSelectedCount
    const virtualizeRows = !!virtualization
    const {
      estimateRowHeight = 41,
      dynamicRowHeight,
      overscan: rowOverscan = 3,
      fixedLayout,
      skeletonWhileScrolling = true,
    } = virtualization && typeof virtualization === 'object' ? virtualization : {}
    const lockColumnWidths = virtualizeRows && (fixedLayout ?? true)
    const hasExpandedRows = rows.some((row) => row.expanded)
    const rowItems = hasExpandedRows ? rows.flatMap((row) => getExpandedRowItems(row)) : null
    const rowItemCount = rowItems ? rowItems.length : rows.length
    const rowsKey = `${rows.length}:${rows[0]?.id ?? ''}`
    const columnsKey = JSON.stringify([
      columnsStore.visibleColumnsIds,
      withActions,
      columnsStore.columns.map((column) => [
        column.field,
        column.width ?? null,
        column.maxWidth ?? null,
        column.minWidth ?? null,
      ]),
    ])
    const pinnedColumns = columnsStore.getPinnedColumns(columnsStore.columns)
    const unpinnedColumns = columnsStore.getUnpinnedColumns(columnsStore.columns)
    const hasLeadingCell = pinnedColumns.length > 0 || Boolean(withCheckbox)
    const hasTrailingCell = Boolean(
      columnsStore.visibleColumnsIds || rightHeaderContent || withActions
    )
    const columnCount =
      (hasLeadingCell ? 1 : 0) + unpinnedColumns.length + (hasTrailingCell ? 1 : 0)
    const windowRows = virtualizeRows

    const { virtualRows, paddingTop, paddingBottom, getRowRef, shouldRenderSkeleton } =
      useVirtualRows({
        enabled: virtualizeRows,
        count: rowItemCount,
        getItemKey: (index) => {
          const row = rows[index]

          return (rowItems ? rowItems[index]?.key : row && getRowItemKey(row)) ?? index
        },
        scrollElement: scrollView,
        estimateRowHeight,
        dynamicRowHeight,
        overscan: rowOverscan,
        rowsKey,
        windowRows,
        skeletonWhileScrolling,
      })

    const onChangePagination = (page: number, limit: number) => {
      store.setPage(page)
      store.setLimit(limit)
      props.onChangePagination?.(page, limit)
    }

    const onScroll = (e: UIEvent) => {
      const target = e.target as HTMLDivElement
      setIsScrollLeft(!!target.scrollLeft)
    }

    const isSelected = (row: TRow) => bulkStore.selectedIds.includes(row.id)

    useEffect(() => {
      store.setRows(rows)

      if (!store.total) store.setTotal(rows.length)
    }, [rows])

    useEffect(() => {
      columnsStore.setPinnedColumnIds(pinnedColumnIds)
      columnsStore.initVisibleColumnsIds()
    }, [])

    useEffect(() => {
      rows.forEach((row) => {
        row.setColumnsKeys(columnsStore.visibleColumnsIds)
      })
    }, [columnsStore.visibleColumnsIds])

    useEffect(() => {
      columnsStore.setColumns(columns)
    }, [columns])

    useEffect(() => {
      columnsStore.setColumnsChild(columnsChild)
    }, [columnsChild])

    if (!columnsStore.isColumnsInitialized) {
      return <SpinnerLoader ariaLabel='TableContent' />
    }

    if (!store.total && !loading) {
      return <EmptyState {...props.emptyStateProps} />
    }

    const buildRowProps = (row: IRow<TRow>): IExpandedTableRowProps<TRow> => ({
      store,
      row,
      selected: isSelected(row),
      inAction: inActionId === row.id,
      withCheckbox,
      hideExpandColumnsIds,
      onRowSelect: store.onRowSelect,
      onRowClick,
      onRowChildClick,
      activeRowId,
      isCheckDate,
      getRowLink,
      getRowChildLink,
      defaultWidthColumn,
      defaultMaxWidthColumn,
      defaultMinWidthColumn,
      defaultColumnCellStyle,
      isScrollLeft,
      isHoveredRowActions,
      isCursor,
      refTable: ref,
    })

    const renderRow = (row: IRow<TRow>, rowRef?: (node: HTMLTableRowElement | null) => void) => (
      <ExpandedTableRow key={row.id} {...buildRowProps(row)} rowRef={rowRef} />
    )

    const renderRowItem = (
      item: IExpandedRowItem<TRow>,
      rowRef?: (node: HTMLTableRowElement | null) => void
    ) => renderExpandedRowItem(item, buildRowProps(getExpandedRowItemGroupRow(item)), rowRef)

    const tableMarkup = (
      <TableMU className={classNames({ [styles.columnWidthLock]: lockColumnWidths })} sx={sxTable}>
        <ColumnWidthLock
          enabled={lockColumnWidths}
          columnsKey={columnsKey}
          columnCount={columnCount}
          scrollElement={scrollView}
          canMeasure={!loading && rows.length > 0 && virtualRows.length > 0}
        />
        <TableHead
          className={classNames(styles.tableHead, {
            [styles['tableHead--hide']]: selected,
          })}
        >
          <TableRowHead
            store={store}
            selected={bulkStore.isSelected}
            withCheckbox={withCheckbox}
            withActions={withActions}
            rightHeaderContent={rightHeaderContent}
            isScrollLeft={isScrollLeft}
            onSort={store.onSort}
            sortBy={store.sortBy}
            sortOrder={store.sortOrder}
          />
        </TableHead>

        <TableBody aria-label='TableBody'>
          {windowRows ? (
            <>
              {paddingTop > 0 && <VirtualSpacerRow colSpan={columnCount} height={paddingTop} />}
              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index]
                const item = rowItems
                  ? rowItems[virtualRow.index]
                  : row
                  ? getExpandedRowItems(row)[0]
                  : undefined

                if (!item) return null
                if (shouldRenderSkeleton(virtualRow.index)) {
                  return (
                    <VirtualSkeletonRow
                      key={item.key}
                      colSpan={columnCount}
                      height={virtualRow.size}
                    />
                  )
                }

                return renderRowItem(item, getRowRef(virtualRow.index))
              })}
              {paddingBottom > 0 && (
                <VirtualSpacerRow colSpan={columnCount} height={paddingBottom} />
              )}
            </>
          ) : (
            rows.map((row) => renderRow(row))
          )}
          {bottomTableContent}
        </TableBody>
      </TableMU>
    )

    const tableContent = (
      <>
        <HeaderSelectedContent
          store={store}
          headerLeftContentProps={headerLeftContentProps}
          withoutSelectedCount={Boolean(withoutSelectedCount)}
          rightHeaderContent={rightHeaderContent}
          withActions={withActions}
          withSelectAll={withSelectAll}
        />
        {tableMarkup}
      </>
    )

    return (
      <Box
        sx={{
          flex: '1 1 auto',
          ...sxBox,
        }}
        height={height}
        width='100%'
        display='flex'
        flexDirection='column'
        position='relative'
        ref={ref}
      >
        {loading ? (
          <SpinnerLoader ariaLabel={`Table_${ariaLabel}`} />
        ) : (
          <>
            <TableContainer style={{ flex: 1 }}>
              <Scrollbar
                scrollToTOpTrigger={scrollToTOpTrigger}
                viewClassName={classNames(styles.viewScrollClassName, {
                  [styles.viewScrollVirtualized]: virtualizeRows,
                })}
                onViewRef={virtualizeRows ? setScrollView : undefined}
                {...scrollbarProps}
                onScroll={(event) => {
                  onScroll(event)
                  scrollbarProps?.onScroll?.(event)
                }}
              >
                {tableContent}
              </Scrollbar>
            </TableContainer>

            {!hidePagination && (
              <Pagination
                total={store.total}
                limit={store.limit}
                initPage={store.page}
                onChange={onChangePagination}
                startSize={store.startLimit}
                usePage={store.config.usePaginationPage}
                hasMore={store.hasMore}
              />
            )}
          </>
        )}
      </Box>
    )
  }
)

export const Table = observer(
  <TRow extends IRowDefault>({ store, ...props }: ITableProps<TRow>) => {
    return <TableContent store={store} {...props} />
  }
)
